/**
 * Wattec — keeps the "Buy now. Pay later." box in step with the variant.
 *
 * The box names each provider's plan rather than quoting an instalment, so
 * there is no arithmetic here to keep right. What is left is the two things
 * that genuinely change with the product in front of the customer:
 *
 *   1. Whether each provider will fund an order this size. The providers'
 *      ceilings differ widely, and a machine can cross one just by changing
 *      variant. Over the ceiling the row keeps its logo and disclosure but
 *      shows that provider's limit instead of an offer the customer would be
 *      refused at checkout. Both lines are already in the page (see
 *      snippets/wattec-finance-terms.liquid); this only swaps which one shows.
 *
 *   2. Whether Shop Pay is offered at all. Shopify publishes that per variant
 *      on the hidden <shopify-payment-terms> element inside the box, which is
 *      far better evidence than a limit we could set ourselves. Where it says
 *      the variant is not eligible, the row comes out.
 *
 * The theme (assets/global.js) sets the hidden input[name="id"] and fires a
 * bubbling change event for both the dropdown and radio pickers, so listening
 * on the document survives the DOM swaps it performs while re-rendering the
 * product information.
 */
(function () {
  if (window.__wattecBnplInit) return;
  window.__wattecBnplInit = true;

  function box() {
    return document.querySelector('[data-wt-bnpl]');
  }

  function priceMap(root) {
    var el = root.querySelector('.wt-bnpl-prices');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * Shopify's plan for one variant, or null when the element is missing — the
   * shop has Shop Pay instalments switched off, or this price is outside its
   * range. The element publishes its data as a JSON attribute, so nothing here
   * waits on Shop Pay's own script having loaded.
   */
  function shopPayPlan(root, variantId) {
    // On the custom-build pages the box is moved into the build rail and this
    // element is left behind outside it, so it is looked for page-wide.
    var el = root.querySelector('shopify-payment-terms') ||
      document.querySelector('.wt-bnpl__official shopify-payment-terms');
    if (!el) return null;

    var meta;
    try {
      meta = JSON.parse(el.getAttribute('shopify-meta') || 'null');
    } catch (e) {
      return null;
    }
    if (!meta || !meta.variants) return null;

    for (var i = 0; i < meta.variants.length; i++) {
      if (String(meta.variants[i].id) === String(variantId)) {
        return meta.variants[i];
      }
    }
    return null;
  }

  /**
   * The running build total beside Add to Cart ("Subtotal inc. taxes"), in
   * pence, where the page has one. It includes the upgrades picked in the
   * options app, so on a custom build it is the figure a lender will actually
   * be asked to fund — a £2,800 base build with £600 of upgrades is over
   * PayPal's £3,000 even though its variant price is not.
   */
  function subtotalPence() {
    var el = document.querySelector('[data-wt-subtotal-value]');
    if (!el) return null;
    var value = parseFloat(String(el.textContent || '').replace(/[^0-9.]/g, ''));
    return isFinite(value) && value > 0 ? Math.round(value * 100) : null;
  }

  /**
   * Klarna's own message works out "3 payments of £x" from the amount it is
   * given, and its app block only knows the variant price. Upgrades picked in
   * the options app raise the real total, so the box hands Klarna the running
   * total instead — Klarna redraws its message, and its "Check purchase power"
   * check, for the price the customer will actually pay.
   */
  function syncKlarna(root, price) {
    var placement = root.querySelector('klarna-placement');
    if (!placement || !price) return;
    if (placement.getAttribute('data-purchase-amount') !== String(price)) {
      placement.setAttribute('data-purchase-amount', String(price));
    }

    // Klarna's app puts the variant price back whenever the options change.
    // Watching the attribute means the running total always wins; it only
    // writes when the two differ, so the two never take turns endlessly.
    if (placement.__wtBnplWatched || !window.MutationObserver) return;
    placement.__wtBnplWatched = true;
    new MutationObserver(function () {
      var wanted = box() && box().dataset.price;
      if (wanted && placement.getAttribute('data-purchase-amount') !== String(wanted)) {
        placement.setAttribute('data-purchase-amount', String(wanted));
      }
    }).observe(placement, { attributes: true, attributeFilter: ['data-purchase-amount'] });
  }

  function watchSubtotal() {
    var el = document.querySelector('[data-wt-subtotal-value]');
    if (!el || el.__wtBnplWatched || !window.MutationObserver) return;
    el.__wtBnplWatched = true;
    new MutationObserver(function () {
      var id = currentVariantId();
      if (id) update(id);
    }).observe(el, { childList: true, characterData: true, subtree: true });
  }

  function setOver(item, over) {
    var plan = item.querySelector('.wt-fterms__plan');
    var limit = item.querySelector('.wt-fterms__limit');
    if (plan) plan.hidden = over;
    if (limit) limit.hidden = !over;
    item.classList.toggle('wt-fterms__item--over', over);
  }

  function update(variantId) {
    var root = box();
    if (!root) return;

    var prices = priceMap(root);
    var price = subtotalPence();
    if (!price) price = prices && prices[variantId];
    if (typeof price !== 'number') price = parseInt(root.dataset.price, 10);
    if (!price || price <= 0) return;

    root.dataset.price = price;
    syncKlarna(root, price);

    root.querySelectorAll('.wt-fterms__item').forEach(function (item) {
      if (item.dataset.provider === 'shopify_pay') {
        var plan = shopPayPlan(root, variantId);
        item.hidden = !!plan && plan.eligible === false;
        return;
      }

      var max = parseInt(item.dataset.max, 10);
      setOver(item, max > 0 && price > max);
    });
  }

  /**
   * The Klarna row, where Klarna's On-site Messaging app is drawing it. Klarna
   * writes its message in after the page loads, and writes nothing at all for
   * a price outside its range or if the app is switched off. Our own Klarna
   * wording waits underneath, hidden, and comes back if Klarna's message has
   * not arrived after a few seconds, so the row is never left empty.
   */
  function watchKlarna() {
    var root = box();
    var item = root && root.querySelector('.wt-fterms__item[data-osm]');
    if (!item) return;

    var osm = item.querySelector('.wt-fterms__osm');
    var fallback = item.querySelector('.wt-fterms__fallback');
    var started = Date.now();

    function drawn() {
      var placement = osm && osm.querySelector('klarna-placement');
      var shadow = placement && placement.shadowRoot;
      var message = shadow && shadow.querySelector('[part="osm-message"]');
      return !!(message && message.textContent.trim());
    }

    (function check() {
      // Klarna's placement is never hidden itself — it takes no room until
      // it draws, and hiding it could stop Klarna drawing it at all.
      if (drawn()) {
        fallback.hidden = true;
        var osmTerms = item.querySelector('.wt-fterms__osm-terms');
        if (osmTerms) {
          osmTerms.hidden = false;
          item.classList.add('has-osm-terms');
        }
        // Klarna's app sets its own amount as it starts up, so the total is
        // handed over again once its message is on the page.
        var id = currentVariantId();
        if (id) update(id);
        return;
      }
      // Past a few seconds our wording fills the row, but Klarna is still
      // given until 20 seconds to arrive on a slow connection and take over.
      if (Date.now() - started > 5000) {
        fallback.hidden = false;
      }
      if (Date.now() - started < 20000) setTimeout(check, 400);
    })();
  }

  /**
   * "Learn more" beside the price glides down to the finance box. The header
   * sticks to the top of the screen, so the box is stopped just below it
   * rather than underneath it, and it glows briefly so the eye lands on it.
   * Anyone who has asked their device to reduce motion jumps straight there.
   */
  function headerOffset() {
    var bottom = 0;
    document.querySelectorAll('sticky-header, .shopify-section-header-sticky, .section-header, header').forEach(function (el) {
      var style = getComputedStyle(el);
      if (style.position !== 'sticky' && style.position !== 'fixed') return;
      var rect = el.getBoundingClientRect();
      if (rect.top <= 1 && rect.bottom > bottom) bottom = rect.bottom;
    });
    return bottom;
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('[data-wt-fin-link]');
    if (!link) return;
    var target = box();
    if (!target) return;
    event.preventDefault();

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset() - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });

    target.focus({ preventScroll: true });
    target.classList.remove('wt-bnpl--flash');
    void target.offsetWidth;
    target.classList.add('wt-bnpl--flash');
  });

  function currentVariantId() {
    var input = document.querySelector('form[action*="/cart/add"] input[name="id"], select[name="id"]');
    if (input && input.value) return input.value;
    return new URLSearchParams(window.location.search).get('variant');
  }

  function init() {
    var id = currentVariantId();
    if (id) update(id);
    watchSubtotal();
    watchKlarna();
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (target && target.name === 'id' && target.value) {
      update(target.value);
      // Klarna's app resets its amount on a variant change too; check again
      // once it has.
      setTimeout(function () { update(target.value); }, 800);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
